import { InputAdornment } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import Link from '@material-ui/core/Link';
import { makeStyles } from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import { useFormik } from 'formik';
import { useEffect, useState } from 'react';
import * as Yup from 'yup';

import {
  Loader,
  OnboardingWrapper,
  VisibilityIcon,
} from '../../shared/components';

type ViewProps = {
  createAccount: (name: string, password: string) => void;
  isLoading: boolean;
  errorMsg: string | null;
};

const useStyles = makeStyles((theme) => ({
  header: {
    fontSize: '19px',
    fontWeight: 600,
    lineHeight: '28.5px',
    letterSpacing: '0.03px',
    marginBottom: '20px',
    color: theme.palette.primary.main,
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
  },
  textFieldsWrapper: {
    padding: '0 14px',
  },
  textField: {
    color: theme.palette.text.secondary,
    fontSize: '13px',
    marginBottom: '30px',
    width: '100%',
    '& label': {
      fontSize: '13px',
    },
  },
  textFieldEmail: {
    marginBottom: '10px',
  },
  passwordRequirements: {
    fontSize: '11px',
    fontWeight: 500,
    lineHeight: '17px',
    color: theme.palette.text.secondary,
    marginLeft: '5%',
    marginBottom: '30px',
    cursor: 'pointer',
  },
  passwordContainer: {
    display: 'flex',
    alignItems: 'center',
    position: 'relative',
  },
  button: {
    width: '100%',
    marginBottom: '15px',
    cursor: 'pointer',
    fontSize: '17px',
    '&:disabled': {
      backgroundColor: '#92C6C1',
      color: 'white',
    },
  },
  privacyWrapper: {
    display: 'flex',
    color: theme.palette.text.secondary,
    flexWrap: 'wrap',
    '& p': {
      fontSize: '13px',
    },
    marginBottom: '24px',
  },
  termsLink: {
    marginLeft: '5px',
    color: theme.palette.text.secondary,
    fontWeight: 700,
    cursor: 'pointer',
  },
}));

const validationSchema = Yup.object().shape({
  password: Yup.string()
    .required('Required')
    .matches(
      /^.*(?=.{8,})(?=.*\d)(?=.*[a-z])(?=.*[A-Z])/,
      'Must Contain at least 8 Characters, One Uppercase, One Lowercase, and One Number'
    ),
  name: Yup.string().required('Required'),
});

const View: React.FC<ViewProps> = ({ createAccount, isLoading, errorMsg }) => {
  const classes = useStyles();
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const initialValues = {
    name: '',
    password: '',
  };
  const [isFormValid, setIsFormValid] = useState<boolean>(false);

  const onSubmit = () => {
    const { name, password } = values;
    createAccount(name, password);
  };

  const {
    errors,
    // setErrors,
    values,
    handleSubmit,
    handleChange,
    isValid,
  } = useFormik({
    initialValues,
    validationSchema,
    onSubmit,
  });

  useEffect(() => {
    if (values !== initialValues) {
      setIsFormValid(isValid);
    }
  }, [isValid, values]);

  if (isLoading) {
    return <Loader />;
  }

  return (
    <OnboardingWrapper error={errorMsg}>
      <>
        <form className={classes.form} onSubmit={handleSubmit}>
          <div className={classes.textFieldsWrapper}>
            <Typography className={classes.header}>
              Create your account
            </Typography>
            <TextField
              className={classes.textField}
              id="name"
              name="name"
              label="Name"
              helperText={errors.name}
              error={Boolean(errors.name)}
              variant="outlined"
              onChange={handleChange}
              value={values.name}
            />
            <TextField
              className={`${classes.textField} ${classes.textFieldEmail}`}
              id="password"
              name="password"
              label="Password"
              type={showPassword ? 'text' : 'password'}
              helperText={errors.password}
              error={Boolean(errors.password)}
              variant="outlined"
              onChange={handleChange}
              value={values.password}
              InputProps={{
                endAdornment: (
                  <InputAdornment
                    position="end"
                    onClick={() => setShowPassword(!showPassword)}>
                    <VisibilityIcon visible={showPassword} />
                  </InputAdornment>
                ),
              }}
            />
            <Typography className={classes.passwordRequirements}>
              Must contain an uppercase letter, a lowercase letter, and a
              number. At least 8 characters.
            </Typography>
          </div>
          <div className={classes.privacyWrapper}>
            <Typography>
              By continuing you agree to our
              <span className={classes.termsLink}>
                <Link href="https://halterix.com/privacy-policy/">
                  Privacy Policy
                </Link>
              </span>{' '}
              and
              <span className={classes.termsLink}>
                <Link href="https://halterix.com/halterix-pt-beta-terms-and-conditions-patients/">
                  Terms and Conditions
                </Link>
              </span>
            </Typography>
          </div>
          <Button
            disabled={!isFormValid}
            color="primary"
            variant="contained"
            type="submit"
            className={classes.button}>
            Create Account
          </Button>
        </form>
      </>
    </OnboardingWrapper>
  );
};

export default View;
